import { useEffect, useState } from 'react';
import { medirTinta } from '../../utils/medirTinta';

/*
 * Piezas compartidas por las tarjetas de producto con franja (la de la sección
 * promocional y la del menú): la franja de porción + tiempo y la etiqueta
 * Bestseller. Cambian los colores de cada tarjeta, no el contenido, así que
 * los colores llegan por className / heredan el color del texto.
 */

export const buildImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

// Porción del producto (Product.portionSize + portionUnit): "1 persona",
// "2 piezas", "200 ml".
const textoPorcion = (n, unidad) => {
  if (unidad === 'ml') return `${n} ml`;
  if (unidad === 'piezas') return `${n} ${n === 1 ? 'pieza' : 'piezas'}`;
  return `${n} ${n === 1 ? 'persona' : 'personas'}`;
};

function IconoPorcion({ unidad }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" className="flex-shrink-0">
      {unidad === 'ml' ? (
        // Vaso
        <path d="M2 1.2h8l-1 9.4a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9Z M3.1 4.2h5.8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      ) : unidad === 'piezas' ? (
        // Cuadro con pieza adentro, el mismo glifo de "Pieces" en la referencia
        <>
          <rect x="1" y="1" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <rect x="3.75" y="3.75" width="4.5" height="4.5" rx="1" fill="currentColor" />
        </>
      ) : (
        // Persona
        <>
          <circle cx="6" cy="3.6" r="2.4" fill="currentColor" />
          <path d="M1.5 11.2c0-2.6 2-4.3 4.5-4.3s4.5 1.7 4.5 4.3Z" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

/**
 * Los dos renglones de la franja: porción y tiempo de preparación. Cada uno se
 * pinta solo si el producto lo trae. Por defecto toman el color del texto del
 * padre; la tarjeta del menú les da color propio.
 *   - conTiempo: false en los no disponibles (la referencia solo deja la porción)
 *   - claseIconoPorcion: color del icono de la porción
 *   - claseTiempo: color del renglón del tiempo (icono y texto)
 */
export function LineasMeta({ product, conTiempo = true, claseIconoPorcion = '', claseTiempo = '' }) {
  return (
    <>
      {product.portionSize > 0 && (
        <span className="flex items-center gap-1 text-[11px] font-medium whitespace-nowrap">
          <span className={`flex ${claseIconoPorcion}`}>
            <IconoPorcion unidad={product.portionUnit} />
          </span>
          {textoPorcion(product.portionSize, product.portionUnit)}
        </span>
      )}
      {conTiempo && product.prepTime > 0 && (
        <span className={`flex items-center gap-1 text-[11px] font-medium whitespace-nowrap ${claseTiempo}`}>
          {/* Reloj "medio lleno": el mismo glifo de la referencia. */}
          <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" className="flex-shrink-0">
            <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M6 1.6A4.4 4.4 0 0 1 6 10.4Z" fill="currentColor" />
          </svg>
          {product.prepTime} mins
        </span>
      )}
    </>
  );
}

// Alto del dibujo de la etiqueta Bestseller, en px. En la referencia mide 22px
// de 267 de tarjeta: ~11.5px en nuestros 140; un pelo más, a petición.
const ALTO_BESTSELLER = 13;

/**
 * Imagen recortada a su dibujo. Los PNG suelen traer mucho aire transparente
 * alrededor (el de Bestseller ocupa 152 de 400px de alto), así que darle alto
 * a la imagen completa la deja diminuta. Se mide la tinta y se escala para que
 * sea el DIBUJO el que mida `alto`, alineado a la izquierda.
 *   - maxAncho: opcional. Si el dibujo a ese alto quedaría más ancho, se
 *     achica hasta caber (iconos, que deben entrar en un cuadro).
 */
export function ImagenRecortada({ url, alt, alto, maxAncho, className = '' }) {
  const [tinta, setTinta] = useState(null);

  useEffect(() => {
    let vivo = true;
    medirTinta(url).then((t) => { if (vivo) setTinta(t); });
    return () => { vivo = false; };
  }, [url]);

  // Sin medición (aún, o la imagen no se pudo leer): la imagen tal cual,
  // contenida en la caja pedida.
  if (!tinta) {
    return (
      <img
        src={url}
        alt={alt}
        className={`w-auto max-w-full object-contain object-left ${className}`}
        style={{ height: alto, ...(maxAncho ? { maxWidth: maxAncho } : {}) }}
      />
    );
  }

  // Proporción del dibujo (ancho / alto) en píxeles reales.
  const proporcion = (tinta.ancho * tinta.aspecto) / tinta.alto;
  const altoDibujo = maxAncho ? Math.min(alto, maxAncho / proporcion) : alto;
  const altoImg = altoDibujo / tinta.alto;
  const anchoImg = altoImg * tinta.aspecto;

  return (
    <div
      className={`relative overflow-hidden max-w-full ${className}`}
      style={{ height: altoDibujo, width: tinta.ancho * anchoImg }}
    >
      <img
        src={url}
        alt={alt}
        className="absolute max-w-none"
        style={{ width: anchoImg, height: altoImg, left: -tinta.x * anchoImg, top: -tinta.y * altoImg }}
      />
    </div>
  );
}

/**
 * Etiqueta "Bestseller" bajo el nombre. La imagen la sube el admin en la
 * sección promocional (promoSection.bestsellerImage); sin ella cae a la
 * versión dibujada, en el color que pase la tarjeta (`claseTexto`).
 */
export function Bestseller({ imagen, claseTexto }) {
  if (imagen) return <ImagenRecortada url={buildImageUrl(imagen)} alt="Bestseller" alto={ALTO_BESTSELLER} className="mt-0.5" />;
  return (
    <span className={`mt-0.5 flex items-center gap-1 text-[13px] font-semibold ${claseTexto}`}>
      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" className="flex-shrink-0">
        <path
          fill="currentColor"
          d="M12 2.5l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 17.52l-5.88 3.09 1.12-6.55L2.48 9.42l6.58-.96L12 2.5z"
        />
      </svg>
      Bestseller
    </span>
  );
}
